import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import {
  useListPlaybooks,
  useStartPlaybook,
  useDeleteCustomPlaybook,
} from "@workspace/api-client-react";
import { useAuth } from "@/hooks/useAuth";
import { useTrialExhaustedDialog } from "@/hooks/use-trial-exhausted-dialog";
import type { Playbook } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { sortPlaybooksByAddedAt } from "@/lib/playbook-utils";
import { PlaybookSwimLane } from "@/components/playbook-swim-lane";
import { PlaybookPreviewSheet, CARD_BACKGROUNDS, type CardBg } from "@/components/playbook-preview-sheet";

/** Must match backend `LIBRARY_TAGS` — library page filters. */
const LIBRARY_FILTER_TAGS = [
  "Health",
  "Finance",
  "Self-care",
  "Self-growth",
  "Tech",
  "Other",
] as const;

export default function LibraryScreen() {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const queryClient = useQueryClient();
  const { data: playbooks, isLoading, refetch: refetchPlaybooks } = useListPlaybooks();
  const [selected, setSelected] = useState<Playbook | null>(null);
  const [selectedCardBg, setSelectedCardBg] = useState<CardBg | null>(null);
  const [startError, setStartError] = useState("");
  const [tagFilter, setTagFilter] = useState<(typeof LIBRARY_FILTER_TAGS)[number] | null>(null);
  const { trialExhaustedDialog, showTrialExhaustedDialog } = useTrialExhaustedDialog();

  useEffect(() => {
    void refetchPlaybooks();
  }, [refetchPlaybooks]);

  const { mutate: startPlaybook, isPending: isStarting } = useStartPlaybook({
    mutation: {
      onSuccess: (data) => {
        setStartError("");
        setLocation(`/processing/${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setStartError("");
          void refetch();
          return;
        }
        setStartError("Something went wrong. Please try again.");
      },
    },
  });

  const { mutate: deletePlaybook } = useDeleteCustomPlaybook({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["listPlaybooks"] });
        setSelected(null);
      },
    },
  });

  const handleCardClick = (playbook: Playbook, bg: CardBg) => {
    setSelected(playbook);
    setSelectedCardBg(bg);
  };

  const allPlaybooks = sortPlaybooksByAddedAt(playbooks ?? []);
  const filteredPlaybooks =
    tagFilter === null ? allPlaybooks : allPlaybooks.filter((p) => p.category === tagFilter);

  return (
    <Layout>
      {trialExhaustedDialog}
      <div className="max-w-7xl mx-auto w-full px-5 py-8">
        <header className="mb-8">
          <h1 className="page-title font-display text-3xl md:text-4xl">
            Unfold triggers.
          </h1>
          <div className="flex items-start justify-between gap-4 mt-5">
            {/* Mobile: minimal dropdown, styled as a filter chip */}
            <div className="relative sm:hidden">
              <select
                value={tagFilter ?? ""}
                onChange={(e) => setTagFilter(e.target.value === "" ? null : (e.target.value as (typeof LIBRARY_FILTER_TAGS)[number]))}
                aria-label="Filter by category"
                className="appearance-none text-xs font-semibold uppercase tracking-[0.14em] pl-3.5 pr-8 py-1.5 rounded-full border border-foreground/12 bg-white text-ink-soft focus:outline-none focus:border-foreground/30"
              >
                <option value="">All</option>
                {LIBRARY_FILTER_TAGS.map((tag) => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            </div>

            {/* Web: pill row */}
            <div className="hidden sm:flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTagFilter(null)}
                className={cn("chip", tagFilter === null && "chip-active")}
              >
                All
              </button>
              {LIBRARY_FILTER_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setTagFilter(tag)}
                  className={cn("chip", tagFilter === tag && "chip-active")}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </header>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="min-h-[7.5rem] rounded-3xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : filteredPlaybooks.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-muted/30 px-6 py-14 text-center">
            <p className="text-muted-foreground text-sm mb-4">Nothing in this category yet.</p>
            <button
              type="button"
              onClick={() => setTagFilter(null)}
              className="text-sm font-medium text-primary hover:underline"
            >
              Show all playbooks
            </button>
          </div>
        ) : (
          <div>
            {/* Swim lanes — All media (no title) → Created → Trending */}
            <PlaybookSwimLane
              playbooks={filteredPlaybooks}
              selectedId={selected?.id}
              onCardClick={(playbook, idx) =>
                handleCardClick(playbook, CARD_BACKGROUNDS[idx % CARD_BACKGROUNDS.length]!)
              }
            />
            <PlaybookSwimLane
              title="Created"
              playbooks={filteredPlaybooks.filter((p) => p.isCustom && p.canDelete)}
              selectedId={selected?.id}
              onCardClick={(playbook, idx) =>
                handleCardClick(playbook, CARD_BACKGROUNDS[idx % CARD_BACKGROUNDS.length]!)
              }
            />
            <PlaybookSwimLane
              title="Trending"
              playbooks={filteredPlaybooks.filter((p) => !p.isCustom)}
              selectedId={selected?.id}
              onCardClick={(playbook, idx) =>
                handleCardClick(playbook, CARD_BACKGROUNDS[idx % CARD_BACKGROUNDS.length]!)
              }
            />
          </div>
        )}
      </div>

      <PlaybookPreviewSheet
        playbook={selected}
        cardBg={selectedCardBg}
        onClose={() => setSelected(null)}
        onStart={() => startPlaybook({ id: selected!.id })}
        isStarting={isStarting}
        startError={startError}
        onDelete={() => deletePlaybook({ id: selected!.id })}
      />
    </Layout>
  );
}
